import { useState, useMemo, useImperativeHandle, forwardRef } from "react";
import { Input, InputNumber, Tooltip, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { stringify } from "svgson";
import { downloadBlob } from "../../utils/nativeDownload";

// Default longest edge of the rendered PNG. Matches the SVG editor's default
// export width so both editors produce comparably sized rasters.
const DEFAULT_PNG_MAX_EDGE = 1500;
const MIN_PNG_EDGE = 16;
const MAX_PNG_EDGE = 8000;

const readViewBox = (svgJoined) => {
  const vb = svgJoined?.attributes?.viewBox?.split(/[\s,]+/).map(Number);
  if (!vb || vb.length < 4 || vb.some((n) => !isFinite(n)) || !vb[2] || !vb[3]) {
    return { width: 1000, height: 1000 };
  }
  return { width: vb[2], height: vb[3] };
};

// btoa() throws on any code point above U+00FF, which SVG text/metadata can
// easily contain — encode to UTF-8 bytes first.
const svgToDataUrl = (svgString) => {
  const bytes = new TextEncoder().encode(svgString);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
};

const renderSvgToPngBlob = (svgString, width, height) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))), "image/png");
    };
    img.onerror = () => reject(new Error("SVG could not be rasterized"));
    img.src = svgToDataUrl(svgString);
  });

const FieldLabel = ({ children }) => (
  <div style={{
    fontSize: "11px", fontWeight: 700, color: "#9ca3af",
    letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: "6px",
  }}>
    {children}
  </div>
);

/**
 * SVG / PNG artwork tabs for the stitch export modal. Exports the design as a
 * plain image — the machine stitch file comes from the Stitch File tab's
 * generate/download flow. Parent drives the download via ref so the button can
 * live in the modal's pinned footer.
 */
const StitchArtworkExport = forwardRef(function StitchArtworkExport({
  svgJoined,
  format,
  exportName,
  onExportNameChange,
  isMobile,
}, ref) {
  const source = useMemo(() => readViewBox(svgJoined), [svgJoined]);
  const aspect = source.width / source.height;
  // `widthInput` holds exactly what's typed, including empty and out-of-range
  // values, so backspacing digits never fights the caret. It's only snapped
  // into range when the field loses focus.
  const [widthInput, setWidthInput] = useState(() =>
    Math.round(aspect >= 1 ? DEFAULT_PNG_MAX_EDGE : DEFAULT_PNG_MAX_EDGE * aspect));
  const clampWidth = (value) => {
    if (!value || !isFinite(value)) {
      return Math.round(aspect >= 1 ? DEFAULT_PNG_MAX_EDGE : DEFAULT_PNG_MAX_EDGE * aspect);
    }
    return Math.min(MAX_PNG_EDGE, Math.max(MIN_PNG_EDGE, Math.round(value)));
  };
  const pngWidth = clampWidth(widthInput);
  const pngHeight = Math.max(1, Math.round(pngWidth / aspect));
  // Mirror whatever is currently typed rather than the committed width, so the
  // height doesn't sit at a stale number while the user is mid-edit.
  const displayHeight = widthInput ? Math.max(1, Math.round(widthInput / aspect)) : null;

  const baseName = (String(exportName || "").replace(/\.[^/.]+$/, "").trim()) || "design";

  const exportSvgString = useMemo(() => {
    if (!svgJoined) return null;
    return stringify({
      ...svgJoined,
      attributes: {
        ...svgJoined.attributes,
        xmlns: svgJoined.attributes?.xmlns || "http://www.w3.org/2000/svg",
        width: String(source.width),
        height: String(source.height),
        // Same marker the SVG editor stamps, so re-importing the file is
        // recognised as our own output instead of being re-vectorized.
        "data-svgtrace": "1",
        "data-svgtrace-version": "1",
      },
    });
  }, [svgJoined, source.width, source.height]);

  const handleExport = async () => {
    if (!exportSvgString) return;
    try {
      if (format === "svg") {
        const blob = new Blob([exportSvgString], { type: "image/svg+xml" });
        await downloadBlob(blob, `${baseName}.svg`);
        message.success(`Downloaded ${baseName}.svg`);
      } else {
        const blob = await renderSvgToPngBlob(exportSvgString, pngWidth, pngHeight);
        await downloadBlob(blob, `${baseName}.png`);
        message.success(`Downloaded ${baseName}.png`);
      }
    } catch (err) {
      console.error(`[StitchArtworkExport] ${format} export failed:`, err);
      message.error(`Couldn't download the ${format.toUpperCase()}. Please try again.`);
    }
  };

  useImperativeHandle(ref, () => ({ export: handleExport }));

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: "4px" }}>
        <h3 style={{ margin: "0 0 2px", fontSize: "16px", fontWeight: 700, fontFamily: "Clash Grotesk, sans-serif", color: "#111" }}>
          {format === "svg" ? "Download the vector artwork" : "Download the artwork as an image"}
        </h3>
        <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
          {format === "svg"
            ? "Scales to any size without losing quality — no stitch generation needed."
            : "A flat image with a transparent background — no stitch generation needed."}
        </p>
      </div>

      {/* Artwork preview */}
      <div style={{
        borderRadius: "10px", border: "1px solid #eef0f2", overflow: "hidden",
        background: "#fff",
        backgroundImage:
          "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "12px", minHeight: isMobile ? "140px" : "180px",
      }}>
        {exportSvgString && (
          <img
            src={svgToDataUrl(exportSvgString)}
            alt="Design preview"
            style={{ maxWidth: "100%", maxHeight: isMobile ? "130px" : "170px", objectFit: "contain" }}
          />
        )}
      </div>

      {/* PNG size controls — SVG is resolution-independent so it needs none */}
      {format === "png" && (
        <div style={{ padding: "14px", background: "#f9fafb", borderRadius: "10px", border: "1px solid #eef0f2" }}>
          <FieldLabel>Image size</FieldLabel>
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>Width (px)</div>
              <InputNumber
                value={widthInput}
                onChange={setWidthInput}
                onBlur={() => setWidthInput(pngWidth)}
                min={MIN_PNG_EDGE}
                max={MAX_PNG_EDGE}
                step={100}
                style={{ width: "100%" }}
              />
            </div>

            <Tooltip title="Height follows the design's proportions">
              <span style={{
                border: "1px solid #e5e7eb", background: "rgba(244,83,196,0.08)",
                padding: "0 8px", height: "32px", borderRadius: "6px", color: "#F453C4",
                display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1px",
              }}>
                <LockOutlined />
              </span>
            </Tooltip>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>Height (px)</div>
              <InputNumber value={displayHeight} disabled style={{ width: "100%" }} />
            </div>
          </div>

          <div style={{
            marginTop: "12px", padding: "8px 12px", background: "#fff",
            borderRadius: "6px", fontSize: "12px", color: "#6b7280",
            display: "flex", justifyContent: "space-between",
          }}>
            <span>Output: <strong style={{ color: "#111" }}>{pngWidth}×{pngHeight} px</strong></span>
            <span>PNG · transparent background</span>
          </div>
        </div>
      )}

      <div style={{ padding: "14px", background: "#f9fafb", borderRadius: "10px", border: "1px solid #eef0f2" }}>
        <FieldLabel>File name</FieldLabel>
        <Input
          value={exportName}
          onChange={(e) => onExportNameChange?.(e.target.value)}
          placeholder="design"
          maxLength={80}
          addonAfter={`.${format}`}
          aria-label="Export file name"
        />
      </div>
    </>
  );
});

export default StitchArtworkExport;
